import React, { useEffect, useState } from 'react';
import { Row, Col, Spin, Button, Modal, Form, Card, Typography, InputNumber, DatePicker, Upload, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { PlusOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { IconShieldCheck, IconPaperclip, IconAlertTriangle } from '@tabler/icons-react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import EmojiTextArea from '../../components/EmojiTextArea';
import {
  getMyHostInsurancePolicies,
  createHostInsurancePolicy,
  updateHostInsurancePolicy,
  deleteHostInsurancePolicy,
  uploadHostInsurancePolicyDocument,
} from '../../services/HostInsurancePolicyService';
import { IHostInsurancePolicy } from '../../interfaces/heavyDutyMachinery/IHostInsurancePolicy';
import { INSURANCE_VERIFICATION_LABEL_KEYS, INSURANCE_VERIFICATION_TONES } from '../../constants/insuranceVerification';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { pillToneStyle } from '../../theme/creamyTokens';

const { Text, Title } = Typography;

interface PolicyFormValues {
  name: string;
  description?: string;
  coverageSummary: string;
  pricePerDay: number;
  insurerName?: string;
  policyNumber?: string;
  expiresOn?: Dayjs;
  excessAmount?: number;
  coverageLimit?: number;
}

/**
 * A host's own catalog of insurance policies -- same "catalog at the host
 * level, reusable across machines" pattern as MyMachineryAccessories (Fase
 * 11), and this page follows that same list-plus-create pattern and
 * HOST_TOOLS_NAV's own host-only nav placement (a peer to "Mi flota"/"Mis
 * accesorios", not one of MobileBottomNav's 5 fixed tabs).
 * Linking a policy to a specific machine happens on that machine's own edit
 * form (see HeavyDutyMachineryEditForm), not here.
 */
const MyHostInsurancePoliciesContent: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [policies, setPolicies] = useState<IHostInsurancePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<IHostInsurancePolicy | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState<IHostInsurancePolicy | undefined>(undefined);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [uploadingPolicyId, setUploadingPolicyId] = useState<string | undefined>(undefined);
  const [form] = Form.useForm<PolicyFormValues>();

  const load = () => {
    setLoading(true);
    getMyHostInsurancePolicies()
      .then(setPolicies)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LOAD_ERROR)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateModal = () => {
    setEditingPolicy(undefined);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (policy: IHostInsurancePolicy) => {
    setEditingPolicy(policy);
    form.setFieldsValue({
      name: policy.name,
      description: policy.description,
      coverageSummary: policy.coverageSummary,
      pricePerDay: policy.pricePerDay,
      insurerName: policy.insurerName,
      policyNumber: policy.policyNumber,
      expiresOn: policy.expiresOn ? dayjs(policy.expiresOn) : undefined,
      excessAmount: policy.excessAmount,
      coverageLimit: policy.coverageLimit,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      coverageSummary: values.coverageSummary.trim(),
      pricePerDay: values.pricePerDay,
      insurerName: values.insurerName?.trim() || undefined,
      policyNumber: values.policyNumber?.trim() || undefined,
      // Date only -- a policy expires on a day, not at an instant, and
      // sending a local midnight as UTC would shift it a day in half of
      // Europe.
      expiresOn: values.expiresOn ? values.expiresOn.format('YYYY-MM-DD') : undefined,
      excessAmount: values.excessAmount ?? undefined,
      coverageLimit: values.coverageLimit ?? undefined,
    };

    setSaving(true);
    try {
      const saved = editingPolicy
        ? await updateHostInsurancePolicy(editingPolicy.id, payload)
        : await createHostInsurancePolicy(payload);
      setPolicies((prev) => (editingPolicy ? prev.map((p) => (p.id === saved.id ? saved : p)) : [saved, ...prev]));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_SAVE_SUCCESS));
      setModalOpen(false);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (policy: IHostInsurancePolicy) => {
    try {
      const updated = await updateHostInsurancePolicy(policy.id, { isActive: !policy.isActive });
      setPolicies((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_STATUS_UPDATE_ERROR));
    }
  };

  const handleUploadDocument = async (policy: IHostInsurancePolicy, file: File) => {
    setUploadingPolicyId(policy.id);
    try {
      const updated = await uploadHostInsurancePolicyDocument(policy.id, file);
      setPolicies((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.INSURANCE_POLICY_DOCUMENT_UPLOAD_ERROR));
    } finally {
      setUploadingPolicyId(undefined);
    }
  };

  const handleDeletePolicy = async () => {
    if (!deletingPolicy) return;
    setDeleteBusy(true);
    try {
      await deleteHostInsurancePolicy(deletingPolicy.id);
      setPolicies((prev) => prev.filter((p) => p.id !== deletingPolicy.id));
      setDeletingPolicy(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_STATUS_UPDATE_ERROR));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_NAV_LABEL)}
            </Title>
            <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_PAGE_SUBTITLE)}</Text>
          </div>
          {!loading && policies.length > 0 && (
            <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={openCreateModal}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_CREATE_BUTTON)}
            </Button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : policies.length === 0 ? (
          <EmptyState
            icon={<IconShieldCheck size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_EMPTY_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_EMPTY_DESCRIPTION)}
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_CREATE_BUTTON)}
              </Button>
            }
          />
        ) : (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {policies.map((policy) => (
              <Col xs={24} sm={12} md={8} key={policy.id}>
                <Card bordered style={{ borderRadius: 18, opacity: policy.isActive ? 1 : 0.6 }} styles={{ body: { padding: 16 } }}>
                  <span
                    style={{
                      display: 'inline-block',
                      alignSelf: 'flex-start',
                      marginBottom: 4,
                      padding: '2px 10px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      ...pillToneStyle(policy.isActive ? 'success' : 'default', token),
                    }}
                  >
                    {translate(t, policy.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_ACTIVE_STATUS : TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_INACTIVE_STATUS)}
                  </span>
                  {/* Only worth a pill once the host has claimed real cover
                      -- a bare description-only option has nothing to
                      verify, so an "Unverified" badge on it would be noise. */}
                  {policy.hasDocument || policy.excessAmount ? (
                    <span
                      style={{
                        display: 'inline-block',
                        marginLeft: 6,
                        marginBottom: 4,
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 600,
                        ...pillToneStyle(INSURANCE_VERIFICATION_TONES[policy.verificationStatus], token),
                      }}
                    >
                      {translate(t, INSURANCE_VERIFICATION_LABEL_KEYS[policy.verificationStatus])}
                    </span>
                  ) : null}

                  <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {policy.name}
                  </Title>
                  {policy.description && (
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      {policy.description}
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
                    {policy.coverageSummary}
                  </Text>
                  <Text strong style={{ display: 'block', marginTop: 10, fontSize: 15 }}>
                    {policy.pricePerDay.toFixed(2)}€/día
                  </Text>

                  {/* The franquicia leads, above the insurer and the policy
                      number, because it is the only one of the three that
                      changes what a renter is exposed to. */}
                  <Text
                    style={{
                      display: 'block',
                      marginTop: 6,
                      fontSize: 13,
                      color: policy.excessAmount ? token.colorText : token.colorWarningText,
                    }}
                  >
                    {policy.excessAmount
                      ? translate(t, TRANSLATIONS.INSURANCE_POLICY_EXCESS_ON_CARD, { amount: `${policy.excessAmount.toFixed(2)}€` })
                      : translate(t, TRANSLATIONS.INSURANCE_POLICY_NO_EXCESS_ON_CARD)}
                  </Text>
                  {(policy.insurerName || policy.policyNumber) && (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                      {[policy.insurerName, policy.policyNumber].filter(Boolean).join(' · ')}
                    </Text>
                  )}

                  {/* A declared excess that nobody has checked protects
                      nobody -- say so here rather than let the host assume
                      otherwise. */}
                  {policy.verificationStatus === 'Pending' && policy.excessAmount ? (
                    <Text type="secondary" style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 8 }}>
                      <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                      {translate(t, TRANSLATIONS.INSURANCE_VERIFICATION_PENDING_HINT)}
                    </Text>
                  ) : null}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(policy)}
                      >
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_EDIT_BUTTON)}
                      </Button>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle(policy.isActive ? 'error' : 'success', token) }}
                        icon={policy.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                        onClick={() => toggleActive(policy)}
                      >
                        {translate(t, policy.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_DEACTIVATE_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_REACTIVATE_BUTTON)}
                      </Button>
                    </div>
                    <Upload
                      accept="application/pdf,image/jpeg,image/png"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        handleUploadDocument(policy, file as File);
                        // Never let antd do its own XHR -- the upload goes
                        // through apiUploadService so it carries the auth
                        // header like every other call in the app.
                        return false;
                      }}
                    >
                      <Button
                        shape="round"
                        block
                        loading={uploadingPolicyId === policy.id}
                        style={{ height: 40, border: 'none', ...pillToneStyle(policy.hasDocument ? 'success' : 'default', token) }}
                        icon={<IconPaperclip size={16} />}
                      >
                        {translate(t, policy.hasDocument ? TRANSLATIONS.INSURANCE_POLICY_DOCUMENT_ATTACHED : TRANSLATIONS.INSURANCE_POLICY_DOCUMENT_LABEL)}
                      </Button>
                    </Upload>
                    <Button
                      shape="round"
                      block
                      style={{ height: 40, border: 'none', ...pillToneStyle('error', token) }}
                      icon={<DeleteOutlined />}
                      onClick={() => setDeletingPolicy(policy)}
                    >
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_BUTTON)}
                    </Button>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Modal
          open={modalOpen}
          title={translate(t, editingPolicy ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_MODAL_EDIT_TITLE : TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_MODAL_CREATE_TITLE)}
          onCancel={() => setModalOpen(false)}
          onOk={handleSave}
          confirmLoading={saving}
          okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_SAVE_BUTTON)}
          okButtonProps={{ shape: 'round' }}
          cancelButtonProps={{ shape: 'round' }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="name" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_NAME_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item name="description">
              <EmojiTextArea placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_DESCRIPTION_LABEL)} rows={2} disabled={saving} maxLength={500} />
            </Form.Item>
            <Form.Item name="coverageSummary" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <EmojiTextArea placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_COVERAGE_SUMMARY_LABEL)} rows={3} disabled={saving} maxLength={2000} />
            </Form.Item>
            <Form.Item label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_PRICE_PER_DAY_LABEL)} name="pricePerDay" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <InputNumber min={0} step={1} addonAfter="€/día" inputMode="decimal" style={{ width: '100%' }} disabled={saving} />
            </Form.Item>

            {/* Everything below describes the REAL policy behind the offer,
                for a host putting their own company's fleet cover on the
                platform. All optional -- a host can still offer a plain
                description-only option -- but without an excess there is
                nothing to cap a damage charge with, which is what the hint
                says out loud. */}
            <div style={{ marginTop: 4, marginBottom: 12, paddingTop: 14, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
              <Text strong style={{ display: 'block', fontSize: 15 }}>
                {translate(t, TRANSLATIONS.INSURANCE_POLICY_REAL_SECTION)}
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {translate(t, TRANSLATIONS.INSURANCE_POLICY_REAL_SECTION_HINT)}
              </Text>
            </div>

            <Form.Item name="insurerName">
              <FloatingLabelInput label={translate(t, TRANSLATIONS.INSURANCE_POLICY_INSURER_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item name="policyNumber">
              <FloatingLabelInput label={translate(t, TRANSLATIONS.INSURANCE_POLICY_NUMBER_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item label={translate(t, TRANSLATIONS.INSURANCE_POLICY_EXPIRES_LABEL)} name="expiresOn">
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled={saving} inputReadOnly />
            </Form.Item>
            <Form.Item
              label={translate(t, TRANSLATIONS.INSURANCE_POLICY_EXCESS_LABEL)}
              name="excessAmount"
              extra={translate(t, TRANSLATIONS.INSURANCE_POLICY_EXCESS_HINT)}
            >
              <InputNumber min={0} step={50} addonAfter="€" inputMode="decimal" style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
            <Form.Item label={translate(t, TRANSLATIONS.INSURANCE_POLICY_COVERAGE_LIMIT_LABEL)} name="coverageLimit">
              <InputNumber min={0} step={1000} addonAfter="€" inputMode="decimal" style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {translate(t, TRANSLATIONS.INSURANCE_POLICY_DOCUMENT_HINT)}
            </Text>
          </Form>
        </Modal>
      </div>

      <ConfirmBottomSheet
        open={!!deletingPolicy}
        title={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_TITLE)}
        description={deletingPolicy?.name}
        cancelText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_KEEP)}
        confirmText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_DELETE)}
        confirmLoading={deleteBusy}
        onConfirm={handleDeletePolicy}
        onClose={() => setDeletingPolicy(undefined)}
      />
    </AppLayout>
  );
};

// Mobile only -- same pattern as Landing/HostProfileEditPage/
// HeavyDutyMachineryForm/MyOperators: everything on this page that reads
// token.colorPrimary (the "New policy" and modal Save buttons, the
// price-per-day text) picks up coral instead of the app-wide black
// primary, without touching desktop at all. Split into a wrapper + Content
// component because theme.useToken() only picks up a nested ConfigProvider's
// tokens when called by a component mounted *under* it, not by the ancestor
// that renders the provider itself.
const MyHostInsurancePolicies: React.FC = () => {
  const { isMobile } = useAppBreakpoint();
  return isMobile ? (
    <MyHostInsurancePoliciesContent />
  ) : (
    <MyHostInsurancePoliciesContent />
  );
};

export default MyHostInsurancePolicies;
